import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ProgressWave } from "@/components/ProgressWave";
import { FileUpload } from "@/components/FileUpload";
import { api, money, formatApiError } from "@/lib/api";

const empty = { name: "", description: "", goal: 0, start_date: "", end_date: "", cover_image: "", active: true };

export default function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/campaigns").then(({ data }) => setItems(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ ...empty, ...c, start_date: c.start_date || "", end_date: c.end_date || "", cover_image: c.cover_image || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, goal: Number(form.goal) || 0 };
      if (editing) await api.put(`/campaigns/${editing}`, payload);
      else await api.post("/campaigns", payload);
      toast.success(editing ? "Campaign updated" : "Campaign created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign and all its teams & donations?")) return;
    await api.delete(`/campaigns/${id}`);
    toast.success("Campaign deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-campaigns">
      <div className="flex items-center justify-between">
        <div>
          <span className="hf-eyebrow">Campaign control</span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#00334a]">Campaigns</h1>
        </div>
        <Button onClick={openNew} className="rounded-full hf-btn-coral font-bold" data-testid="admin-create-campaign-button">
          <Plus className="mr-2 h-4 w-4" /> New campaign
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <Megaphone className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No campaigns yet</p>
          <p className="text-sm text-[#7c7a6a]">Create your first campaign to start building teams.</p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {items.map((c) => {
            const pct = c.goal ? Math.min(100, Math.round((c.raised / c.goal) * 100)) : 0;
            return (
              <div key={c.id} className="overflow-hidden rounded-3xl border border-[#e4d8c4] bg-white" data-testid={`campaign-card-${c.id}`}>
                {c.cover_image ? (
                  <img src={c.cover_image} alt={c.name} className="h-32 w-full object-cover" />
                ) : <div className="h-3 hf-gradient-deep" />}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-heading text-xl font-bold text-[#00334a]">{c.name}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-[#7c7a6a]">{c.description}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)} className="h-8 w-8 text-[#3f6670]" data-testid={`campaign-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(c.id)} className="h-8 w-8 text-[#c97049]" data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="mt-4"><ProgressWave pct={pct} height="h-3" showMilestones /></div>
                  <div className="mt-2 flex justify-between text-sm font-semibold">
                    <span className="text-[#65949c]">{money(c.raised)}</span>
                    <span className="text-[#7c7a6a]">of {money(c.goal)}</span>
                  </div>
                  <div className="mt-3 flex gap-4 text-xs text-[#7c7a6a]">
                    <span>{c.team_count} teams</span><span>{c.supporter_count} supporters</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="campaign-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit campaign" : "New campaign"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="campaign-name-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="campaign-description-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Goal ($)</Label><Input type="number" min="0" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="campaign-goal-input" /></div>
              <div className="space-y-1.5">
                <Label>Cover image</Label>
                <div className="flex items-center gap-3">
                  <FileUpload scope="cover" label={form.cover_image ? "Replace image" : "Upload image"} testId="campaign-cover-upload"
                    onUploaded={(d) => setForm({ ...form, cover_image: d.url })} />
                  {form.cover_image && <img src={form.cover_image} alt="cover" className="h-12 w-20 rounded-lg object-cover" />}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="rounded-full hf-btn-coral font-bold" data-testid="campaign-save-button">{busy ? "Saving…" : "Save campaign"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
